import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import axios from '../../plugins/axios';
import type { AppDispatch, RootState } from '../index';

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

interface Template { templateId: number | string; name?: string; html?: string; [k: string]: any }
interface Section { sectionId: number | string; key?: string; html?: string; [k: string]: any }
interface Placeholder { key: string; [k: string]: any }

interface ConfigState {
    serverValues: Record<string, unknown>;
    placeholders: Placeholder[];
    templates: Record<string, Template>;
    sections: Record<string, Section>;
    documentTemplateId: number | string | null;
}

const initialState: ConfigState = {
    serverValues: {},
    placeholders: [],
    templates: {},
    sections: {},
    documentTemplateId: null,
};

const configSlice = createSlice({
    name: 'config',
    initialState,
    reducers: {
        setServerValues(state, action: PayloadAction<Record<string, unknown>>) {
            state.serverValues = action.payload;
        },
        setTemplateInfo(state, action: PayloadAction<{ placeholders: Placeholder[]; templates: Template[]; sections: Section[] }>) {
            state.placeholders = action.payload.placeholders;
            state.templates = _.keyBy(action.payload.templates, (x) => x.templateId);
            state.sections = _.keyBy(action.payload.sections, (x) => x.sectionId);
        },
        setTemplate(state, action: PayloadAction<Template>) {
            state.templates[String(action.payload.templateId)] = action.payload;
        },
        setSection(state, action: PayloadAction<Section>) {
            state.sections[String(action.payload.sectionId)] = action.payload;
        },
        setDocumentTemplateId(state, action: PayloadAction<number | string | null>) {
            state.documentTemplateId = action.payload;
        },
    },
});

export const {
    setServerValues,
    setTemplateInfo,
    setTemplate,
    setSection,
    setDocumentTemplateId,
} = configSlice.actions;

// Orchestration thunks — left as plain thunk functions because RTK's
// configureStore wires thunk middleware in by default. Components dispatch them
// the same way they dispatch slice actions.
export const getTemplateInfo = () => async (dispatch: AppDispatch) => {
    const response = await axios.get(`${apiUrl}/gettemplateinfo`);
    dispatch(setTemplateInfo(response.data));
};

export const getSection = (sectionId: number | string) => async (dispatch: AppDispatch) => {
    try {
        const response = await axios.get(`${apiUrl}/getsection/${sectionId}`);
        dispatch(setSection(response.data));
    } catch {
        // Retry once after 1s — preserves the legacy behaviour where the API
        // occasionally returns transient errors during template publishing.
        setTimeout(async () => {
            const response = await axios.get(`${apiUrl}/getsection/${sectionId}`);
            dispatch(setSection(response.data));
        }, 1000);
    }
};

// ---- Memoised selectors. Without these, `useAppSelector(state => Object.values(...))`
// returns a fresh array on every render and react-redux warns about the unstable
// reference. createSelector caches the derived value across renders, only recomputing
// when the input slice actually changes.
const sectionsRecord = (state: RootState) => state.configReducer.sections;
const templatesRecord = (state: RootState) => state.configReducer.templates;

const sortByKey = (a: Section, b: Section) =>
    (a.key === b.key) ? 0 : ((a.key ?? '') < (b.key ?? '') ? -1 : 1);
const sortByName = (a: Template, b: Template) =>
    (a.name === b.name) ? 0 : ((a.name ?? '') < (b.name ?? '') ? -1 : 1);

export const selectSections = createSelector([sectionsRecord], (s) => Object.values(s));
export const selectSectionsSortedByKey = createSelector([sectionsRecord], (s) =>
    Object.values(s).sort(sortByKey)
);
export const selectTemplatesSortedByName = createSelector([templatesRecord], (t) =>
    Object.values(t).sort(sortByName)
);

export default configSlice.reducer;
